// Port of SuiteModel/Revisions.swift and the reads of DocumentStore+Revisions.swift: revision mode (review B17). The
// writer starts a revision ("Second draft"), which records a checkpoint; from then on the text shows what changed since
// the first revision began, the words added in each revision in its own colour, until the changes are accepted or
// rejected. Kept in the project (JSON `settings.revisions`, through the settings-JSON rules), so every device shows the
// same marks. Offsets are UTF-16 (as the editor counts). Exact: `RevisionDiff` aligns words with the Swift's own Myers
// diff (history/collectionDifference.ts), so hunks, folding and levels are the Swift's.
import * as A from '@automerge/automerge';
import { decodeHeadsIn } from '../automerge/heads.ts';
import { ROOT, SwiftReader } from '../automerge/swiftDoc.ts';
import { fragmentDocName } from '../edit/editProject.ts';
import { difference } from '../history/collectionDifference.ts';
import { readProject, type ProjectRecord } from '../model/documents.ts';
import { fromISO8601 } from '../review/json.ts';
import { decodeJSON, lossyArray, mergedJSON } from '../settingsJSON.ts';
import type { Range16 } from '../text/scalars.ts';

export interface Revision {
  id: string;
  name: string;
  /** The checkpoint recorded when the revision began: the text as it stood before. */
  checkpointID: string;
  started: Date;
}

/** A change accepted into the base: replace `length` UTF-16 units at `location` (which read `deleted`) with `text`.
 *  Offsets are into the base as it stood when the change was accepted (after the patches before it). */
export interface RevisionPatch {
  location: number;
  length: number;
  text: string;
  /** What the base read at the range when the change was accepted (absent in patches from older builds). */
  deleted?: string;
}

export interface Revisions {
  /** Oldest first; the first revision's checkpoint is the base the changes are measured from. */
  revisions: Revision[];
  /** Changes accepted since the base, per fragment, in the order they were accepted. */
  accepted: Record<string, RevisionPatch[]>;
}

export const emptyRevisions = (): Revisions => ({ revisions: [], accepted: {} });
export const isRevisionActive = (r: Revisions) => r.revisions.length > 0;
export const currentRevision = (r: Revisions): Revision | null => r.revisions[r.revisions.length - 1] ?? null;

/** Whether a patch still fits `text`: the range is inside it and reads what was replaced. */
export function patchApplies(p: RevisionPatch, text: string): boolean {
  if (p.location < 0 || p.length < 0 || p.location + p.length > text.length) return false;
  return p.deleted === undefined || text.slice(p.location, p.location + p.length) === p.deleted;
}

/** `Revisions.apply`: patches in order, skipping any that no longer fit rather than guessing where they went. */
export function applyPatches(patches: readonly RevisionPatch[], text: string): string {
  let result = text;
  for (const p of patches) if (patchApplies(p, result)) result = result.slice(0, p.location) + p.text + result.slice(p.location + p.length);
  return result;
}

/** `Revisions.base`: a fragment's text at the first revision's checkpoint with the accepted changes applied. */
export const revisionBase = (r: Revisions, checkpointText: string, fragmentID: string) => applyPatches(r.accepted[fragmentID] ?? [], checkpointText);

// MARK: JSON (Codable, with the Swift's tolerance)

const str = (o: Record<string, unknown>, k: string) => { const v = o[k]; if (typeof v !== 'string') throw new Error(k); return v; };
const int = (o: Record<string, unknown>, k: string) => { const v = o[k]; if (typeof v !== 'number' || !Number.isInteger(v)) throw new Error(k); return v; };
const obj = (v: unknown) => { if (!v || typeof v !== 'object' || Array.isArray(v)) throw new Error('object'); return v as Record<string, unknown>; };

function decodeRevision(v: unknown): Revision {
  const o = obj(v);
  return { id: str(o, 'id'), name: str(o, 'name'), checkpointID: str(o, 'checkpointID'), started: fromISO8601(o.started) };
}

function decodePatch(v: unknown): RevisionPatch {
  const o = obj(v);
  const deleted = o.deleted;
  if (deleted !== undefined && deleted !== null && typeof deleted !== 'string') throw new Error('deleted');
  return { location: int(o, 'location'), length: int(o, 'length'), text: str(o, 'text'), ...(typeof deleted === 'string' ? { deleted } : {}) };
}

/** `Revisions(from:)`: revisions and patches this build can't read are left out; an `accepted` that isn't a map of
 *  arrays reads as none. */
export function decodeRevisions(json: unknown): Revisions {
  const o = obj(json);
  const accepted: Record<string, RevisionPatch[]> = {};
  if (o.accepted && typeof o.accepted === 'object' && !Array.isArray(o.accepted) && Object.values(o.accepted).every(Array.isArray)) {
    for (const [id, list] of Object.entries(o.accepted as Record<string, unknown[]>)) accepted[id] = lossyArray(list, decodePatch);
  }
  return { revisions: lossyArray(o.revisions, decodeRevision), accepted };
}

export const encodeRevisions = (r: Revisions) => ({ revisions: r.revisions, accepted: r.accepted });

/** `ProjectDocument.revisions()`. */
export function readRevisions(project: ProjectRecord): Revisions {
  return decodeJSON(project.settings.revisions, decodeRevisions) ?? emptyRevisions();
}

/** `setRevisions`' stored string (throws `ModelError.invalid`, changing nothing, when the stored value can't be read). */
export const revisionsJSON = (stored: unknown, value: Revisions) =>
  mergedJSON(stored, value, decodeRevisions, 'revisions', encodeRevisions);

// MARK: Changes

/** One change between a fragment's base and its current text (UTF-16 [from, to)). */
export interface RevisionChange {
  /** Where the change sits in the current text (empty for words only deleted). */
  current: Range16;
  /** What it replaced in the base (empty for words only added). */
  base: Range16;
  inserted: string;
  deleted: string;
  /** The added words by revision: ranges in the current text, each with the index of the revision it was written in. */
  levels: { range: Range16; level: number }[];
  /** The revision a deletion is shown in. */
  deletionLevel: number;
}

export const changeID = (c: RevisionChange) => `${c.current[0]}:${c.current[1] - c.current[0]}:${c.base[0]}:${c.base[1] - c.base[0]}`;

/** `acceptPatch`: the patch that makes the base agree with this change. */
export const acceptPatch = (c: RevisionChange): RevisionPatch => ({ location: c.base[0], length: c.base[1] - c.base[0], text: c.inserted, deleted: c.deleted });

interface Hunk { old: Range16; new: Range16; levels: { range: Range16; level: number }[] }

/** A word with the spaces after it, or a run of line breaks. Words compare without their spaces; `word` is where the
 *  word itself ends. */
interface Token { range: Range16; word: number; key: string }

const isSpace = (u: number) => u === 0x20 || u === 0x09 || u === 0xa0;
const isBreak = (u: number) => u === 0x0a || u === 0x0d || u === 0x2028 || u === 0x2029;

export function revisionTokens(s: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = s.length;
  while (i < n) {
    const start = i;
    if (isBreak(s.charCodeAt(i))) {
      let breaks = 0;
      while (i < n && (isBreak(s.charCodeAt(i)) || isSpace(s.charCodeAt(i)))) {
        if (s.charCodeAt(i) === 0x0a) breaks++;
        i++;
      }
      tokens.push({ range: [start, i], word: i, key: `¶${breaks}` });
      continue;
    }
    while (i < n && !isSpace(s.charCodeAt(i)) && !isBreak(s.charCodeAt(i))) i++;
    const wordEnd = i;
    while (i < n && isSpace(s.charCodeAt(i))) i++;
    tokens.push({ range: [start, i], word: wordEnd, key: s.slice(start, wordEnd) });
  }
  return tokens;
}

/** `RevisionDiff.hunks`: aligned changes, short common words between two changes folded into one change. */
function hunks(old: string, next: string): Hunk[] {
  const a = revisionTokens(old), b = revisionTokens(next);
  let prefix = 0;
  while (prefix < a.length && prefix < b.length && a[prefix].key === b[prefix].key) prefix++;
  let suffix = 0;
  while (suffix < a.length - prefix && suffix < b.length - prefix && a[a.length - 1 - suffix].key === b[b.length - 1 - suffix].key) suffix++;
  const am = a.slice(prefix, a.length - suffix), bm = b.slice(prefix, b.length - suffix);
  const removed = new Set<number>(), inserted = new Set<number>();
  for (const c of difference(am.map((t) => t.key), bm.map((t) => t.key))) (c.type === 'remove' ? removed : inserted).add(c.offset + prefix);
  // Walk both token lists, grouping each run of removals and insertions into a hunk (token indices).
  const groups: { old: [number, number]; new: [number, number] }[] = [];
  let i = prefix, j = prefix;
  const aEnd = a.length - suffix, bEnd = b.length - suffix;
  while (i < aEnd || j < bEnd) {
    if ((i < aEnd && removed.has(i)) || (j < bEnd && inserted.has(j))) {
      const oi = i, oj = j;
      do {
        while (i < aEnd && removed.has(i)) i++;
        while (j < bEnd && inserted.has(j)) j++;
      } while ((i < aEnd && removed.has(i)) || (j < bEnd && inserted.has(j)));
      groups.push({ old: [oi, i], new: [oj, j] });
    } else {
      i++;
      j++;
    }
  }
  // Fold short common runs between changes into one change.
  const merged: { old: [number, number]; new: [number, number] }[] = [];
  for (const g of groups) {
    const last = merged[merged.length - 1];
    if (last) {
      const gapOld: [number, number] = [last.old[1], g.old[0]], gapNew: [number, number] = [last.new[1], g.new[0]];
      let gapText = 0, paragraph = false;
      for (let k = gapOld[0]; k < gapOld[1]; k++) {
        gapText += a[k].range[1] - a[k].range[0];
        if (a[k].key.startsWith('¶')) paragraph = true;
      }
      const count = gapOld[1] - gapOld[0];
      if (count === gapNew[1] - gapNew[0] && count <= 2 && gapText <= 8 && !paragraph) {
        merged[merged.length - 1] = { old: [last.old[0], g.old[1]], new: [last.new[0], g.new[1]] };
        continue;
      }
    }
    merged.push(g);
  }
  // A change runs from the end of the word before it to the end of its last token; spaces both sides share are trimmed.
  const span = (tokens: Token[], [lo, hi]: [number, number], fallback: number): Range16 => {
    const start = lo > 0 ? tokens[lo - 1].word : lo < tokens.length ? tokens[lo].range[0] : fallback;
    const end = hi === lo ? (lo < tokens.length ? tokens[lo].range[0] : fallback) : tokens[hi - 1].range[1];
    return [start, start + Math.max(0, end - start)];
  };
  const out: Hunk[] = [];
  for (const g of merged) {
    const hunk: Hunk = { old: span(a, g.old, old.length), new: span(b, g.new, next.length), levels: [] };
    trim(hunk, old, next);
    if (old.slice(hunk.old[0], hunk.old[1]) !== next.slice(hunk.new[0], hunk.new[1])) out.push(hunk);
  }
  return out;
}

/** Drops the spaces a change shares with what it replaced, at either end ("cat " → "dog " marks "cat" → "dog"). */
function trim(h: Hunk, old: string, next: string) {
  while (h.old[1] > h.old[0] && h.new[1] > h.new[0] && old.charCodeAt(h.old[0]) === next.charCodeAt(h.new[0]) && isSpace(old.charCodeAt(h.old[0]))) {
    h.old = [h.old[0] + 1, h.old[1]];
    h.new = [h.new[0] + 1, h.new[1]];
  }
  while (h.old[1] > h.old[0] && h.new[1] > h.new[0] && old.charCodeAt(h.old[1] - 1) === next.charCodeAt(h.new[1] - 1) && isSpace(old.charCodeAt(h.old[1] - 1))) {
    h.old = [h.old[0], h.old[1] - 1];
    h.new = [h.new[0], h.new[1] - 1];
  }
}

/** Splits an added range by revision: each part takes the latest revision whose "added since" covers it. */
function split(range: Range16, since: Range16[][]): { range: Range16; level: number }[] {
  if (!since.length) return [{ range, level: 0 }];
  const parts: { range: Range16; level: number }[] = [];
  const level = (offset: number) => {
    for (let k = since.length - 1; k >= 0; k--) if (since[k].some(([a, b]) => offset >= a && offset < b)) return k + 1;
    return 0;
  };
  let location = range[0];
  const end = range[1];
  while (location < end) {
    const l = level(location);
    let next = location + 1;
    while (next < end && level(next) === l) next++;
    const last = parts[parts.length - 1];
    if (last && last.level === l && last.range[1] === location) last.range = [last.range[0], next];
    else parts.push({ range: [location, next], level: l });
    location = next;
  }
  return parts;
}

/** `RevisionDiff.changes`: from `base` to `current`. `boundaries` are the texts at the later revisions' checkpoints
 *  (oldest first): added words take the latest revision they don't appear before. */
export function revisionChanges(base: string, current: string, boundaries: readonly string[] = []): RevisionChange[] {
  if (base === current) return [];
  const found = hunks(base, current);
  if (!found.length) return [];
  const since = boundaries.map((b) => hunks(b, current).map((h) => h.new).filter(([x, y]) => y > x));
  for (const h of found) if (h.new[1] > h.new[0]) h.levels = split(h.new, since);
  return found.map((h) => ({
    current: h.new, base: h.old, inserted: current.slice(h.new[0], h.new[1]), deleted: base.slice(h.old[0], h.old[1]),
    levels: h.levels, deletionLevel: h.levels[0]?.level ?? boundaries.length,
  }));
}

export const RevisionDiff = { changes: revisionChanges, tokens: revisionTokens };

// MARK: The card: the change at the caret, and the ones either side

/** The change the caret (UTF-16) is in or touches, else null. */
export function changeAt(changes: readonly RevisionChange[], caret: number): RevisionChange | null {
  return changes.find((c) => caret >= c.current[0] && caret <= c.current[1]) ?? null;
}

/** The next change after `caret` (wrapping to the first), for ⌥⌘]. */
export function nextChange(changes: readonly RevisionChange[], caret: number): RevisionChange | null {
  return changes.find((c) => c.current[0] > caret) ?? changes[0] ?? null;
}

/** The change before `caret` (wrapping to the last), for ⌥⌘[. */
export function previousChange(changes: readonly RevisionChange[], caret: number): RevisionChange | null {
  for (let k = changes.length - 1; k >= 0; k--) if (changes[k].current[1] < caret) return changes[k];
  return changes[changes.length - 1] ?? null;
}

/** What Reject puts back for a change, as a replacement of the current text (UTF-16) for the editor to apply undoably:
 *  the base's words in the change's place. */
export function rejectReplacement(change: RevisionChange, base: string): { from: number; to: number; text: string } {
  return { from: change.current[0], to: change.current[1], text: base.slice(change.base[0], change.base[1]) };
}

// MARK: Reads over the documents (DocumentStore+Revisions)

/** `RevisionTexts`: a fragment's base, and its text at each later revision's checkpoint. */
export interface RevisionTexts { base: string; boundaries: string[] }

export const changesIn = (t: RevisionTexts, current: string) => revisionChanges(t.base, current, t.boundaries);

/** A fragment's current text as stored. */
export function storedText(doc: A.Doc<unknown>): string {
  return new SwiftReader(doc).string(ROOT, 'text') ?? '';
}

/** `revisionTexts(of:in:)`: null when revision mode is off, a revision's checkpoint is missing, or its heads for this
 *  fragment haven't synced (the marks would be guesses). A fragment made since a checkpoint was empty then. */
export function revisionTexts(docs: ReadonlyMap<string, A.Doc<unknown>>, fragmentID: string, project?: ProjectRecord): RevisionTexts | null {
  const record = project ?? readProject(docs.get('project')!);
  const revisions = readRevisions(record);
  if (!isRevisionActive(revisions)) return null;
  const doc = docs.get(fragmentDocName(fragmentID));
  if (!doc) return null;
  const checkpoints = new Map<string, (typeof record.checkpoints)[number]>();
  for (const c of record.checkpoints) if (!checkpoints.has(c.id)) checkpoints.set(c.id, c);
  const texts: string[] = [];
  for (const revision of revisions.revisions) {
    const checkpoint = checkpoints.get(revision.checkpointID);
    if (!checkpoint) return null;
    const hex = checkpoint.fragmentHeads[fragmentID];
    if (hex === undefined) { texts.push(''); continue; }
    if (hex === '') { texts.push(''); continue; }
    const heads = decodeHeadsIn(hex, doc);
    if (!heads) return null;
    const r = new SwiftReader(doc, heads);
    const t = r.get(ROOT, 'text');
    texts.push(t && t[0] === 'text' ? r.text(t[1] as string) : '');
  }
  return { base: revisionBase(revisions, texts[0], fragmentID), boundaries: texts.slice(1) };
}

/** `revisionChanges(of:in:)`: the changes since the base (null when its history hasn't synced or mode is off).
 *  `current`: the editor's text, else the stored one. */
export function fragmentRevisionChanges(docs: ReadonlyMap<string, A.Doc<unknown>>, fragmentID: string, current?: string, project?: ProjectRecord): RevisionChange[] | null {
  const texts = revisionTexts(docs, fragmentID, project);
  const doc = docs.get(fragmentDocName(fragmentID));
  if (!texts || !doc) return null;
  return changesIn(texts, current ?? storedText(doc));
}

/** `revisionCounts`: how many changes each fragment has (fragments with none left out). */
export function revisionCounts(docs: ReadonlyMap<string, A.Doc<unknown>>, fragmentIDs: readonly string[]): Record<string, number> {
  const project = readProject(docs.get('project')!);
  const out: Record<string, number> = {};
  for (const id of fragmentIDs) {
    const changes = fragmentRevisionChanges(docs, id, undefined, project);
    if (changes && changes.length) out[id] = changes.length;
  }
  return out;
}

/** The word next to an offset in its paragraph (null at the paragraph's edge); whitespace doesn't count. */
function wordBefore(s: string, offset: number): string | null {
  const start = Math.max(0, offset - 200);
  const lines = s.slice(start, offset).split(/\r\n|[\n\r\u000b\u000c\u0085\u2028\u2029]/u);
  const words = lines[lines.length - 1].split(/\p{White_Space}+/u).filter(Boolean);
  return words[words.length - 1] ?? null;
}

function wordAfter(s: string, offset: number): string | null {
  const end = Math.min(s.length, offset + 200);
  const line = s.slice(offset, end).split(/\r\n|[\n\r\u000b\u000c\u0085\u2028\u2029]/u)[0];
  return line.split(/\p{White_Space}+/u).filter(Boolean)[0] ?? null;
}

/** `DocumentStore.fits`: whether a change still sits between the same words in the base and the current text. */
export function changeFits(change: RevisionChange, base: string, current: string): boolean {
  if (change.current[1] > current.length || current.slice(change.current[0], change.current[1]) !== change.inserted) return false;
  return wordBefore(base, change.base[0]) === wordBefore(current, change.current[0])
    && wordAfter(base, change.base[1]) === wordAfter(current, change.current[1]);
}
